/**
 * Epg entity class
 */

import moment from 'moment'
import { has } from '../../../app/helpers/object'

/**
 * EpgFactory
 * returns Epg entity class
 *
 * @class EpgFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Epg} class
 *
 * @ngdoc factory
 * @name mwApp.factory:EpgFactory
 */
/* @ngInject */
export default function EpgFactory ($http, $q, $injector, accessParser, alertLiteSvc, Entity, onOffMixin) {
  /* === Task class === */

  /**
   * Epg model
   *
   * @class Epg
   * @classdesc Epg model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.onOffMixin
   * @return {models.Epg} Epg entity
   */
  function Epg () {
    const obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  Epg.prototype = Object.create(Entity.prototype)
  Epg.prototype.constructor = Epg

  /* === Epg instance methods & fields === */

  Object.assign(Epg.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Task#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: String,
      enabled: false,
      lastRun: moment,
      nextRun: moment,
      scheduleType: 'HOURLY',
      lastExecutionResult: String,
      shiftPostfix: String,
      sourceUrl: String,
      startTime: String,
      timezone: String,
      username: String,
      password: String,
      $executionResults: []
    },

    /**
     * url template
     *
     * @memberof models.Epg#
     * @type {String}
     */
    _urlTemplate: 'api/tasks/epg/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.Epg#
     * @type {String}
     */
    _publicUrlTemplate: 'epgs/{{::id}}',

    /**
     * Run Epg, performing PATCH request to the server
     *
     * @memberof models.Epg#
     * @return {Promise} promise
     */
    _run: function () {
      return $http.patch(this._url, '"RUN"')
        .catch(alertLiteSvc.alertError)
    },

    /**
     * Get underlying object
     *
     * @memberof models.Epg#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      return Entity.prototype._object.apply(this, arguments)
    }

  }, onOffMixin)

  /**
   * Generate url
   *
   * @param {Object} [o] - object over which Url will be interpolated
   * @param {String} [uri]
   * @return {String}
   */
  Epg.generateUrl = function (uri = '', o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    let url = cast.prototype.$_urlTemplate(o)
    url = url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url
    return url + uri
  }

  Epg.loadEPGS = function (tableState, o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    const url = Epg.generateUrl('', o)

    // if there is access defined in entity class -> check 'view' access
    if (cast._name && cast.access && cast.access.view && !accessParser(cast.access.view).check()) {
      // return empty array for smart table in case user doesn't have 'view' access
      return $q.when({ content: [] })
    }

    const params = {
      'sort-by': tableState.sort.predicate ? tableState.sort.predicate : '',
      'sort-order': tableState.sort.reverse === false ? 'asc' : 'desc',
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
      count: tableState.pagination.number || 25
    }
    Object.assign(params, tableState.search.predicateObject)

    if (params.sourceUrl) {
      params['source-url'] = params.sourceUrl
      delete params.sourceUrl
    }

    if (params.lastRun) {
      params['last-run-after'] = JSON.parse(params.lastRun).after && moment(JSON.parse(params.lastRun).after).format('YYYY-MM-DDTHH:mm:ss[Z]')
      params['last-run-before'] = JSON.parse(params.lastRun).before && moment(JSON.parse(params.lastRun).before).format('YYYY-MM-DDTHH:mm:ss[Z]')
      delete params.lastRun
    }
    if (params.nextRun) {
      params['next-run-after'] = JSON.parse(params.nextRun).after && moment(JSON.parse(params.nextRun).after).format('YYYY-MM-DDTHH:mm:ss[Z]')
      params['next-run-before'] = JSON.parse(params.nextRun).before && moment(JSON.parse(params.nextRun).before).format('YYYY-MM-DDTHH:mm:ss[Z]')
      delete params.nextRun
    }
    if (params.executionResult) {
      params['execution-result'] = params.executionResult
      delete params.executionResult
    }

    return $http.get(url, { params })
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          const result = this.cast(data, {
            // response structure
            content: data.content,
            first: true,
            last: true,
            number: 0,
            numberOfElements: 0,
            size: 0,
            sort: [{
              ascending: true,
              direction: '',
              ignoreCase: true,
              nullHandling: '',
              property: ''
            }],
            totalElements: 0,
            totalPages: 0
          })

          // change SmartTable state
          if (Array.isArray(result.sort) && result.sort.length) {
            const last = result.sort.pop()
            tableState.sort.predicate = last.property
            tableState.sort.reverse = !last.ascending
          }
          tableState.pagination.start = result.number * result.size // first element
          tableState.pagination.number = result.size // page size
          tableState.pagination.numberOfPages = result.totalPages
          tableState.pagination.totalItemCount = result.totalElements

          // return array for SmartTable
          return result
        }
      ).catch(alertLiteSvc.alertError)
  }

  Epg.loadER = function (tableState, o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    const url = Epg.generateUrl('/reports', o)

    // if there is access defined in entity class -> check 'view' access
    if (cast._name && cast.access && cast.access.view && !accessParser(cast.access.view).check()) {
      // return empty array for smart table in case user doesn't have 'view' access
      return $q.when({ content: [] })
    }

    const params = {
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
      count: tableState.pagination.number || 25
    }
    Object.assign(params, tableState.search.predicateObject)

    return $http.get(url, { params })
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          const result = this.cast(data, {
            // response structure
            content: data.content,
            first: true,
            last: true,
            number: 0,
            numberOfElements: 0,
            size: 0,
            sort: [{
              ascending: true,
              direction: '',
              ignoreCase: true,
              nullHandling: '',
              property: ''
            }],
            totalElements: 0,
            totalPages: 0
          })

          // change SmartTable state
          if (Array.isArray(result.sort) && result.sort.length) {
            const last = result.sort.pop()
            tableState.sort.predicate = last.property
            tableState.sort.reverse = !last.ascending
          }
          tableState.pagination.start = result.number * result.size // first element
          tableState.pagination.number = result.size // page size
          tableState.pagination.numberOfPages = result.totalPages
          tableState.pagination.totalItemCount = result.totalElements

          // return array for SmartTable
          return result
        }
      ).catch(alertLiteSvc.alertError)
  }

  /* === Task static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(Epg, Entity)
  Epg._name = 'Epg'
  Epg._publicName = 'EPG XML task'
  Epg.init()

  // permissions
  Epg.access = {
    view: 'viewEpgTask',
    create: 'createEpgTask',
    edit: 'editEpgTask',
    delete: 'deleteEpgTask'
  }


  return Epg
}
